import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useFocusEffect, useRoute } from '@react-navigation/native';

import { ClampingTool, OperationSearchItem, ProductionBatchStatistics } from '@interfaces/search';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { Popup } from '@helpers';
import ClampingToolDetails from '@components/clampingTool/components/clampingToolDetails';
import { useNCFileManagement } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationNcFilesScreen/useNCFileManagement';
import { getTools, getToolsForManyFiles, close } from '@reducers/clampingTools';
import { incrementTabCount, setOperatorPanelTabs, selectTabs } from '@reducers/operatorPanel';
import { operationsSearch } from '@reducers/search';
import { selectSingleBatchData } from '@reducers/batch';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { ToolsDetailsScreenName } from '@v2/pages/tools/details/navigation/config';
import { ToolsDetailsScreen } from '@v2/pages/tools/details/common/enums/toolsDetails.enum';
import { Perspective } from '@v2/pages/common/enums';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { OPERATOR_PANEL_INFORMATION_SCREENS, scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import type { OperatorPanelInformationToolsScreenProps } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationToolsScreen/index';
import { buildToolsTableConfig } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationToolsScreen/helpers/buildToolsTableConfig';
import { OPERATIONS_SEARCH_SELECT_FIELDS } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationToolsScreen/constants';

interface UseManageOperatorPanelInformationToolsScreenParams extends Pick<OperatorPanelInformationToolsScreenProps, 'C'> {}

export const useManageOperatorPanelInformationToolsScreen = ({ C }: UseManageOperatorPanelInformationToolsScreenParams) => {
  const dispatch = useAppDispatch();

  const batch: ReducerEntityWithLoadingState<ProductionBatchStatistics | null> = useSelector(selectSingleBatchData);
  const tabs = useSelector(selectTabs);

  const [tools, setTools] = useState<ClampingTool[]>([]);

  const [jobs, setJobs] = useState<ReducerEntityWithLoadingState<OperationSearchItem[]>>({ data: [], wasLoaded: false });

  const route = useRoute();
  const selectedJobId = route.params?.[scheduledProductionOperationIdParam];

  const jobIds = selectedJobId != null ? [selectedJobId] : [...new Set(batch?.data?.links?.map((l) => l.productionOperationId))];

  const jobIdsHash = jobIds.join(', ');

  useFocusEffect(
    useCallback(() => {
      const toolTabIndex = tabs.findIndex((t) => t.value === OPERATOR_PANEL_INFORMATION_SCREENS.Tools);
      if (toolTabIndex === -1) return;

      const shouldUpdateToolTab = tabs[toolTabIndex].count !== tools.length;
      if (!shouldUpdateToolTab) return;

      const updatedTabs = tabs.map((t, i) => (i !== toolTabIndex ? t : { ...t, count: tools.length }));

      dispatch(setOperatorPanelTabs(updatedTabs));
    }, [tools.length])
  );

  useFocusEffect(
    useCallback(() => {
      if (jobIds.length === 0) {
        return;
      }

      (async () => {
        const jobsData = await dispatch(
          operationsSearch({
            data: {
              select: OPERATIONS_SEARCH_SELECT_FIELDS,
            },
            filter: { oDataFilter: `ProductionOperationId eq ${jobIds.join(' or ProductionOperationId eq ')}`, orderBy: ['ModifiedOn desc'] },
            select: null,
            resetHistory: true,
            ignoreHistory: true,
          })
        );

        if (jobsData?.searchResult?.length) {
          const jobsResult = { data: jobsData.searchResult, wasLoaded: true };
          setJobs(jobsResult);
        }
      })();
    }, [jobIdsHash])
  );

  // Stable reference so useNCFileManagement's memoized callbacks don't churn (connect supplied a stable fn before)
  const handleIncrementTabCount = useCallback((value: any) => dispatch(incrementTabCount(value)), [dispatch]);

  // Use the exact same NC file management logic as the NC Files screen
  const { currentNCFile, currentNCFileLoaded, isMachinePerspective, ncFileListProps, ncFiles } = useNCFileManagement({
    setJobs,
    jobs,
    batch, // Use batch data just like NC Files screen
    incrementTabCount: handleIncrementTabCount,
  });

  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const theme = C.scheme === 'light' ? 'Light' : 'Dark';
  const bg = C(`Background/${theme}/Panel [mobile]`, false);
  const isAllMode = currentNCFile === 'all';
  // Fetch tools when current NC file changes
  useEffect(() => {
    (async () => {
      if (currentNCFile) {
        let fetchedTools;
        if (isAllMode) {
          fetchedTools = await dispatch(getToolsForManyFiles(ncFiles.map((i) => i.ncFileId).join(',')));
        } else {
          fetchedTools = await dispatch(getTools({ ncFileId: currentNCFile.ncFileId }));
        }
        setTools(fetchedTools ?? []);
      } else {
        await dispatch(close());
        setTools([]);
      }
    })();
  }, [currentNCFile]);

  const handleAddTool = () => {
    Popup({
      Component: (p: any) => (
        <ClampingToolDetails
          {...{
            ...p,
            params: { ncFile: currentNCFile?.ncFileName, ncFileId: currentNCFile?.ncFileId },
            newTool: true,
            afterSubmit: async () => {
              const fetchedTools = await dispatch(getTools({ ncFileId: currentNCFile.ncFileId }));
              setTools(fetchedTools ?? []);
              dispatch(incrementTabCount({ tabName: OPERATOR_PANEL_INFORMATION_SCREENS.Tools }));
            },
          }}
        />
      ),
    });
  };

  const goToToolDetails = (tool: ClampingTool) => {
    navigateToMainDetailsScreen({
      mainScreen: ToolsDetailsScreenName,
      subScreen: ToolsDetailsScreen.TOOLS,
      params: {
        perspective: Perspective.TOOLS,
        toolId: tool.toolId,
      },
    });
  };

  const toolsTableConfig = buildToolsTableConfig({ C });

  const isLoading = ((isMachinePerspective && !batch.wasLoaded) || (!isMachinePerspective && !jobs.wasLoaded)) && !currentNCFileLoaded;

  return {
    bg,
    isLoading,
    currentNCFile,
    isAllMode,
    ncFileListProps,
    tools,
    toolsTableConfig,
    handleAddTool,
    goToToolDetails,
  };
};
